import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { 
  Clock, 
  Calendar, 
  User, 
  Tag, 
  Search, 
  ArrowRight,
  BookOpen,
  TrendingUp,
  Eye
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric"
  });
};

const BlogPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  const currentCategory = searchParams.get("category") || "";
  const currentTag = searchParams.get("tag") || "";
  const currentSearch = searchParams.get("search") || "";

  useEffect(() => {
    fetchArticles();
    fetchCategories();
    fetchPopularTags();
    if (!currentCategory && !currentTag && !currentSearch) {
      fetchFeaturedArticles();
    }
  }, [currentCategory, currentTag, currentSearch]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currentCategory) params.append("category", currentCategory);
      if (currentTag) params.append("tag", currentTag);
      if (currentSearch) params.append("search", currentSearch);
      params.append("limit", "12");
      
      const response = await axios.get(`${API}/blog/articles?${params.toString()}`);
      setArticles(response.data.articles);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedArticles = async () => {
    try {
      const response = await axios.get(`${API}/blog/articles/featured?limit=3`);
      setFeaturedArticles(response.data);
    } catch (error) {
      console.error("Error fetching featured:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/blog/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchPopularTags = async () => {
    try {
      const response = await axios.get(`${API}/blog/popular-tags`);
      setPopularTags(response.data);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSearchParams({ search: searchQuery.trim() });
    }
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchQuery("");
  };

  const getCategoryName = (slug) => {
    const cat = categories.find(c => c.slug === slug);
    return cat?.name || slug;
  };

  const pageTitle = currentCategory 
    ? `${getCategoryName(currentCategory)} Articles` 
    : currentTag 
    ? `Articles tagged "${currentTag}"` 
    : currentSearch 
    ? `Search results for "${currentSearch}"` 
    : "Real Estate Blog & Guides";

  const fullTitle = `${pageTitle} | RERA Approved`;

  // SEO - Set document title
  useEffect(() => {
    document.title = fullTitle;
    
    // Set meta tags
    const setMeta = (name, content, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };
    
    setMeta('description', 'Expert articles on RERA regulations, homebuying guides, home loans, market trends, and property investment tips. Stay informed with RERA Approved.');
    setMeta('keywords', 'RERA, real estate blog, homebuying guide, property investment, home loan tips, market trends India');
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', 'Expert articles on RERA regulations, homebuying guides, and property investment tips.', true);
    setMeta('og:type', 'website', true);
  }, [fullTitle]);

  return (
    <>

      <div className="min-h-screen bg-stone-50" data-testid="blog-page">
        {/* Hero Section */}
        <section className="bg-emerald-900 py-16 md:py-20">
          <div className="section-container">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-800/50 text-emerald-200 px-4 py-2 rounded-full text-sm mb-6">
                <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                Knowledge Center
              </div>
              <h1 className="font-display text-3xl md:text-5xl text-white mb-4">
                {currentCategory || currentTag || currentSearch ? pageTitle : "Real Estate Insights & Guides"}
              </h1>
              <p className="text-emerald-100/80 text-lg mb-8">
                Expert articles on RERA compliance, homebuying, finance, and market trends
              </p>
              
              {/* Search */}
              <form onSubmit={handleSearch} className="max-w-xl mx-auto">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" strokeWidth={1.5} />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search articles..."
                      className="pl-12 h-12 bg-white border-0 text-stone-800 placeholder:text-stone-400"
                    />
                  </div>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-white h-12 px-6">
                    Search
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>

        {/* Active Filters */}
        {(currentCategory || currentTag || currentSearch) && (
          <div className="bg-white border-b">
            <div className="section-container py-4">
              <div className="flex items-center justify-between">
                <p className="text-stone-600">
                  Showing {total} article{total !== 1 ? 's' : ''} 
                  {currentCategory && <span> in <strong>{getCategoryName(currentCategory)}</strong></span>}
                  {currentTag && <span> tagged <strong>"{currentTag}"</strong></span>}
                  {currentSearch && <span> matching <strong>"{currentSearch}"</strong></span>}
                </p>
                <Button variant="ghost" onClick={clearFilters} className="text-emerald-700 hover:text-emerald-800">
                  Clear filters
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="section-container py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-3">
              {/* Featured Articles (only on main blog page) */}
              {!currentCategory && !currentTag && !currentSearch && featuredArticles.length > 0 && (
                <div className="mb-12">
                  <h2 className="font-display text-2xl text-stone-800 mb-6">Featured Articles</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {featuredArticles.map((article, idx) => (
                      <Link
                        key={article.article_id}
                        to={`/blog/${article.slug}`}
                        className={`group relative overflow-hidden rounded-sm ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                        data-testid={`featured-article-${idx}`}
                      >
                        <div className={`relative ${idx === 0 ? 'aspect-[16/10]' : 'aspect-[16/9]'}`}>
                          <img
                            src={article.featured_image}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                          <div className="absolute bottom-0 left-0 right-0 p-6">
                            <span className="inline-block bg-amber-500 text-white text-xs uppercase tracking-widest px-3 py-1 mb-3">
                              {getCategoryName(article.category)}
                            </span>
                            <h3 className={`font-display text-white mb-2 group-hover:text-amber-400 transition-colors ${idx === 0 ? 'text-2xl' : 'text-lg'}`}>
                              {article.title}
                            </h3>
                            <div className="flex items-center gap-4 text-white/70 text-sm">
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" strokeWidth={1.5} />
                                {article.read_time} min read
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" strokeWidth={1.5} />
                                {article.views?.toLocaleString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* All Articles */}
              <div>
                <h2 className="font-display text-2xl text-stone-800 mb-6">
                  {currentCategory || currentTag || currentSearch ? 'Articles' : 'Latest Articles'}
                </h2>
                
                {loading ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-white rounded-sm overflow-hidden animate-pulse">
                        <div className="aspect-[16/9] bg-stone-200" />
                        <div className="p-6">
                          <div className="h-4 bg-stone-200 rounded w-1/4 mb-3" />
                          <div className="h-6 bg-stone-200 rounded w-3/4 mb-2" />
                          <div className="h-4 bg-stone-200 rounded w-full" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : articles.length === 0 ? (
                  <div className="bg-white p-12 rounded-sm text-center">
                    <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" strokeWidth={1.5} />
                    <h3 className="font-display text-xl text-stone-800 mb-2">No articles found</h3>
                    <p className="text-stone-500 mb-4">Try adjusting your search or filters</p>
                    <Button onClick={clearFilters} className="bg-emerald-700 hover:bg-emerald-800 text-white">
                      View all articles
                    </Button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {articles.map((article) => (
                      <article
                        key={article.article_id}
                        className="bg-white rounded-sm overflow-hidden shadow-sm hover:shadow-md transition-shadow group"
                        data-testid="blog-article-card"
                      >
                        <Link to={`/blog/${article.slug}`}>
                          <div className="aspect-[16/9] overflow-hidden">
                            <img
                              src={article.featured_image}
                              alt={article.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        </Link>
                        <div className="p-6">
                          <div className="flex items-center gap-3 mb-3">
                            <Link
                              to={`/blog?category=${article.category}`}
                              className="text-xs uppercase tracking-widest text-emerald-700 hover:text-emerald-800 font-medium"
                            >
                              {getCategoryName(article.category)}
                            </Link>
                            <span className="text-stone-300">•</span>
                            <span className="text-xs text-stone-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" strokeWidth={1.5} />
                              {article.read_time} min
                            </span>
                          </div>
                          <Link to={`/blog/${article.slug}`}>
                            <h3 className="font-display text-lg text-stone-800 mb-2 group-hover:text-emerald-700 transition-colors line-clamp-2">
                              {article.title}
                            </h3>
                          </Link>
                          <p className="text-stone-600 text-sm line-clamp-2 mb-4">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm text-stone-500">
                              <User className="w-4 h-4" strokeWidth={1.5} />
                              {article.author}
                            </div>
                            <Link
                              to={`/blog/${article.slug}`}
                              className="text-emerald-700 text-sm font-medium flex items-center gap-1 hover:gap-2 transition-all"
                            >
                              Read <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              {/* Categories */}
              <div className="bg-white p-6 rounded-sm shadow-sm mb-6">
                <h3 className="font-display text-lg text-stone-800 mb-4">Categories</h3>
                <ul className="space-y-2">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/blog?category=${cat.slug}`}
                        className={`flex items-center justify-between py-2 px-3 rounded-sm transition-colors ${
                          currentCategory === cat.slug
                            ? 'bg-emerald-50 text-emerald-700'
                            : 'hover:bg-stone-50 text-stone-600'
                        }`}
                      >
                        <span>{cat.name}</span>
                        <span className="text-xs bg-stone-100 px-2 py-1 rounded-full">
                          {cat.article_count}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Popular Tags */}
              <div className="bg-white p-6 rounded-sm shadow-sm mb-6">
                <h3 className="font-display text-lg text-stone-800 mb-4">Popular Topics</h3>
                <div className="flex flex-wrap gap-2">
                  {popularTags.map((item) => (
                    <Link
                      key={item.tag}
                      to={`/blog?tag=${encodeURIComponent(item.tag)}`}
                      className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-colors ${
                        currentTag === item.tag
                          ? 'bg-emerald-700 text-white'
                          : 'bg-stone-100 text-stone-600 hover:bg-emerald-50 hover:text-emerald-700'
                      }`}
                    >
                      <Tag className="w-3 h-3" strokeWidth={1.5} />
                      {item.tag}
                    </Link>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="bg-emerald-900 p-6 rounded-sm text-white">
                <TrendingUp className="w-8 h-8 text-amber-400 mb-4" strokeWidth={1.5} />
                <h3 className="font-display text-lg mb-2">Looking to Invest?</h3>
                <p className="text-emerald-100/80 text-sm mb-4">
                  Explore RERA-verified properties across 6 metro cities with zero brokerage.
                </p>
                <Link
                  to="/search"
                  className="inline-flex items-center gap-2 bg-white text-emerald-900 px-4 py-2 text-sm font-medium hover:bg-stone-50 transition-colors"
                >
                  Explore Properties <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPage;
